import type { WorkbenchTabOwnership } from '../stores/workbench';

/**
 * A few browser tabs may each run a WebContainer (PROTOTYPER_STABILITY_TECH_PLAN.md P6.4e,
 * decision 2026-09-15). Each one holds hundreds of MB, so the number is capped per device
 * (`workbenchTabSlots`): one Web Lock per slot, held for as long as the tab's WebContainer lives;
 * the browser frees it when the tab closes. Slot 0 keeps the P2.4 lock name.
 *
 * A tab that finds every slot taken asks over the `ideoz-workbench-tabs` BroadcastChannel:
 * 1. `who-owns`: every slot holder answers with its state (busy, showing a workbench, hidden for).
 * 2. `take-over` to one chosen holder only, so one request never empties several tabs:
 *    - `if-idle` (sent automatically on boot): the holder lets go only when it shows no workbench
 *      or has been hidden for `HIDDEN_IDLE_MS`.
 *    - `force` ("Use it here"): the holder lets go unless it is mid-build.
 * The holder answers `released`, `in-use` or `busy`; a released holder tears its WebContainer down.
 *
 * Plan: PROTOTYPER_STABILITY_TECH_PLAN.md §6 (P2.4) and §10 (P6.4e).
 */

export const WORKBENCH_TAB_LOCK = 'ideoz-workbench';
export const WORKBENCH_TAB_CHANNEL = 'ideoz-workbench-tabs';

export const IF_IDLE_REPLY_TIMEOUT_MS = 1_500;
export const FORCE_REPLY_TIMEOUT_MS = 5_000;
/** How long a tab collects `owner-state` answers before choosing which holder to ask. */
export const OWNER_POLL_MS = 250;
/** A workbench hidden this long counts as idle for an automatic take-over. */
export const HIDDEN_IDLE_MS = 10 * 60_000;
const LOCK_RETRY_MS = 100;
const LOCK_WAIT_AFTER_RELEASE_MS = 3_000;

type TakeOverMode = 'if-idle' | 'force';

export interface OwnerState {
  tabId: string;
  busy: boolean;
  inUse: boolean;
  hiddenForMs: number;
}

type TabMessage =
  | { type: 'who-owns'; from: string }
  | ({ type: 'owner-state'; to: string } & OwnerState)
  | { type: 'take-over'; mode: TakeOverMode; from: string; target?: string }
  | { type: 'released' | 'in-use' | 'busy'; to: string };

type TakeOverReply = 'released' | 'in-use' | 'busy' | 'no-answer';

/** The Web Lock for a slot; slot 0 is the single lock of P2.4. */
export function slotLockName(slot: number): string {
  return slot === 0 ? WORKBENCH_TAB_LOCK : `${WORKBENCH_TAB_LOCK}:${slot}`;
}

/**
 * Live workbenches this device may run at once: two on a `full` device, one otherwise.
 * `VITE_WORKBENCH_MAX_TABS` (1–4) overrides it.
 */
export function workbenchTabSlots(
  tier: 'full' | 'best-effort' | 'view-only',
  override?: string | null
): number {
  const fromEnv = override != null && override !== '' ? Number(override) : Number.NaN;
  if (Number.isInteger(fromEnv) && fromEnv >= 1 && fromEnv <= 4) return fromEnv;
  return tier === 'full' ? 2 : 1;
}

/**
 * The slot holder to ask, or null when none may be asked. Never a tab mid-build; `if-idle` only
 * a tab that shows no workbench or has been hidden for `hiddenIdleMs`. Prefers a tab showing no
 * workbench, then the one hidden longest.
 */
export function pickTakeOverCandidate(
  owners: ReadonlyArray<OwnerState>,
  mode: TakeOverMode,
  hiddenIdleMs: number = HIDDEN_IDLE_MS
): OwnerState | null {
  const eligible = owners.filter(
    (owner) =>
      !owner.busy && (mode === 'force' || !owner.inUse || owner.hiddenForMs >= hiddenIdleMs)
  );
  if (eligible.length === 0) return null;
  return [...eligible].sort(
    (a, b) => Number(a.inUse) - Number(b.inUse) || b.hiddenForMs - a.hiddenForMs
  )[0]!;
}

/** The subset of `LockManager` used here, so tests can pass an in-memory one. */
export interface LockManagerLike {
  request(
    name: string,
    options: { ifAvailable: boolean },
    callback: (lock: unknown) => Promise<void> | void
  ): Promise<unknown>;
}

export interface ChannelLike {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  removeEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
}

export interface TabOwnershipDeps {
  tabId: string;
  /** `null` when the browser has no Web Locks: every tab acts as owner (no enforcement). */
  locks: LockManagerLike | null;
  channel: ChannelLike | null;
  /** Live workbenches this device may run (`workbenchTabSlots`); default 1. */
  slots?: number;
  /** This tab currently shows a workbench (a conversation linked the WebContainer). */
  isInUse: () => boolean;
  /** This tab is mid-build; a take-over is refused so the build is not killed. */
  isBusy: () => boolean;
  /** How long this tab has been hidden, 0 while visible; default always 0. */
  hiddenForMs?: () => number;
  /** Called after this tab gave its slot to another tab; tear the WebContainer down here. */
  onReleased: () => void;
  onStateChange: (state: WorkbenchTabOwnership) => void;
  sleep?: (ms: number) => Promise<void>;
  /** How long to collect `owner-state` answers; default `OWNER_POLL_MS`. */
  ownerPollMs?: number;
}

export interface TabOwnership {
  /** Called before boot. `takeOverIdleTab: false` (prewarm) never asks another tab. */
  ensure(opts: { takeOverIdleTab: boolean }): Promise<boolean>;
  /** "Use it here". `owner`: this tab now holds a slot; reload to boot cleanly. */
  claim(): Promise<'owner' | 'busy' | 'no-answer'>;
}

export function createTabOwnership(deps: TabOwnershipDeps): TabOwnership {
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const slots = Math.max(1, deps.slots ?? 1);
  const hiddenForMs = deps.hiddenForMs ?? (() => 0);
  const ownerPollMs = deps.ownerPollMs ?? OWNER_POLL_MS;
  let release: (() => void) | null = null;

  const tryLockSlot = (locks: LockManagerLike, slot: number): Promise<boolean> =>
    new Promise((resolve) => {
      locks
        .request(slotLockName(slot), { ifAvailable: true }, (lock) => {
          if (!lock) {
            resolve(false);
            return undefined;
          }
          return new Promise<void>((done) => {
            release = () => {
              release = null;
              done();
            };
            resolve(true);
          });
        })
        // A broken Locks implementation must not lock the founder out of the workbench.
        .catch(() => resolve(true));
    });

  const tryLock = async (): Promise<boolean> => {
    if (release || !deps.locks) return true;
    for (let slot = 0; slot < slots; slot++) {
      if (await tryLockSlot(deps.locks, slot)) return true;
    }
    return false;
  };

  const waitForLock = async (timeoutMs: number): Promise<boolean> => {
    const attempts = Math.max(1, Math.ceil(timeoutMs / LOCK_RETRY_MS));
    for (let i = 0; i < attempts; i++) {
      if (await tryLock()) return true;
      await sleep(LOCK_RETRY_MS);
    }
    return false;
  };

  deps.channel?.addEventListener('message', (event) => {
    const msg = event.data as TabMessage | null;
    if (!msg || !release) return;

    if (msg.type === 'who-owns') {
      if (msg.from === deps.tabId) return;
      deps.channel?.postMessage({
        type: 'owner-state',
        to: msg.from,
        tabId: deps.tabId,
        busy: deps.isBusy(),
        inUse: deps.isInUse(),
        hiddenForMs: hiddenForMs(),
      } satisfies TabMessage);
      return;
    }

    if (msg.type !== 'take-over' || msg.from === deps.tabId) return;
    // Addressed to another holder.
    if (msg.target !== undefined && msg.target !== deps.tabId) return;
    const reply = (type: 'released' | 'in-use' | 'busy'): void =>
      deps.channel?.postMessage({ type, to: msg.from } satisfies TabMessage);

    if (deps.isBusy()) return reply('busy');
    if (msg.mode === 'if-idle' && deps.isInUse() && hiddenForMs() < HIDDEN_IDLE_MS) {
      return reply('in-use');
    }

    release();
    deps.onStateChange('released');
    deps.onReleased();
    reply('released');
  });

  const collectOwners = (): Promise<OwnerState[]> => {
    const channel = deps.channel;
    if (!channel) return Promise.resolve([]);
    return new Promise((resolve) => {
      const owners = new Map<string, OwnerState>();
      const listener = (event: { data: unknown }): void => {
        const msg = event.data as TabMessage | null;
        if (!msg || msg.type !== 'owner-state' || msg.to !== deps.tabId) return;
        owners.set(msg.tabId, {
          tabId: msg.tabId,
          busy: msg.busy,
          inUse: msg.inUse,
          hiddenForMs: msg.hiddenForMs,
        });
      };
      channel.addEventListener('message', listener);
      channel.postMessage({ type: 'who-owns', from: deps.tabId } satisfies TabMessage);
      setTimeout(() => {
        channel.removeEventListener('message', listener);
        resolve([...owners.values()]);
      }, ownerPollMs);
    });
  };

  const askOwner = (
    target: string,
    mode: TakeOverMode,
    timeoutMs: number
  ): Promise<TakeOverReply> => {
    const channel = deps.channel;
    if (!channel) return Promise.resolve('no-answer');
    return new Promise((resolve) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const listener = (event: { data: unknown }): void => {
        const msg = event.data as TabMessage | null;
        if (
          !msg ||
          msg.type === 'take-over' ||
          msg.type === 'who-owns' ||
          msg.type === 'owner-state' ||
          msg.to !== deps.tabId
        ) {
          return;
        }
        finish(msg.type);
      };
      const finish = (reply: TakeOverReply): void => {
        if (timer !== undefined) clearTimeout(timer);
        channel.removeEventListener('message', listener);
        resolve(reply);
      };
      timer = setTimeout(() => finish('no-answer'), timeoutMs);
      channel.addEventListener('message', listener);
      channel.postMessage({
        type: 'take-over',
        mode,
        from: deps.tabId,
        target,
      } satisfies TabMessage);
    });
  };

  /** Ask the one holder most able to let go; `busy` when every holder is mid-build. */
  const requestRelease = async (mode: TakeOverMode, timeoutMs: number): Promise<TakeOverReply> => {
    const owners = await collectOwners();
    if (owners.length === 0) return 'no-answer';
    const candidate = pickTakeOverCandidate(owners, mode);
    if (!candidate) return owners.every((owner) => owner.busy) ? 'busy' : 'in-use';
    return askOwner(candidate.tabId, mode, timeoutMs);
  };

  const becomeOwner = (): true => {
    deps.onStateChange('owner');
    return true;
  };

  return {
    async ensure({ takeOverIdleTab }) {
      if (await tryLock()) return becomeOwner();
      if (takeOverIdleTab) {
        const reply = await requestRelease('if-idle', IF_IDLE_REPLY_TIMEOUT_MS);
        if (reply === 'released' && (await waitForLock(LOCK_WAIT_AFTER_RELEASE_MS))) {
          return becomeOwner();
        }
        deps.onStateChange(reply === 'busy' ? 'other-tab-busy' : 'other-tab');
        return false;
      }
      deps.onStateChange('other-tab');
      return false;
    },

    async claim() {
      if (await tryLock()) {
        becomeOwner();
        return 'owner';
      }
      const reply = await requestRelease('force', FORCE_REPLY_TIMEOUT_MS);
      if (reply === 'busy') {
        deps.onStateChange('other-tab-busy');
        return 'busy';
      }
      if (reply === 'released' && (await waitForLock(LOCK_WAIT_AFTER_RELEASE_MS))) {
        becomeOwner();
        return 'owner';
      }
      deps.onStateChange('other-tab');
      return 'no-answer';
    },
  };
}

export class WorkbenchTabInUseError extends Error {
  constructor() {
    super('The prototype workspace is open in another tab.');
    this.name = 'WorkbenchTabInUseError';
  }
}
